import IconChevronDown from "@shared/components/icons/ChevronDown"
import IconHeadingBig from "@shared/components/icons/HeadingBig"
import IconHeadingSmall from "@shared/components/icons/HeadingSmall"
import IconHeadings from "@shared/components/icons/Headings"
import { Button } from "@shared/components/untitledui/base/buttons/button"
import { Dropdown } from "@shared/components/untitledui/base/dropdown/dropdown"
import { Tooltip } from "@shared/components/untitledui/base/tooltip/tooltip"
import { cx } from "@shared/components/untitledui/util/cx"
import { OPERATION_CONTRACTS } from "@shared/unified-tiptap/analytics/operations"
import {
  isBlockTypeActive,
  setBlockType,
  toggleBlockType,
} from "@shared/unified-tiptap/util/block-node-helpers"
import { modKeyText } from "@shared/unified-tiptap/util/is-apple"
import type { Editor } from "@tiptap/core"
import { useEditorState } from "@tiptap/react"
import { Pilcrow02 } from "@untitledui/icons"
import type { ReactNode } from "react"
import type { Key, Selection } from "react-aria-components"
import {
  MENU_ROW_GLYPH,
  SELECTED_ROW,
  TOOLBAR_BUTTON_ACTIVE,
  TOOLBAR_BUTTON_CHROME,
  TOOLBAR_CHEVRON,
} from "./toolbar-button-chrome"

// The dropdown is a single-selection menu: each block style is a key, exactly the current
// block's style reads selected (aria-checked). react-aria owns the radio-group semantics;
// re-selecting the active style deselects it, which we map back to a plain paragraph — the
// same "toggle a heading off" the action menu did, now announced to assistive tech.
const HEADING_ITEMS = [
  { key: "big", label: "Big Heading", level: 3, shortcut: 1, glyph: <IconHeadingBig /> },
  { key: "small", label: "Small Heading", level: 4, shortcut: 2, glyph: <IconHeadingSmall /> },
] as const

/** The style key describing the current block, or null when it's something else/mixed. */
function activeHeadingKey(editor: Editor): Key | null {
  for (const item of HEADING_ITEMS) {
    if (isBlockTypeActive(editor.state, "heading", { level: item.level })) return item.key
  }
  return isBlockTypeActive(editor.state, "paragraph") ? "normal" : null
}

export function HeadingDropdownMenu({ editor }: { editor: Editor }) {
  const activeKey = useEditorState({ editor, selector: () => activeHeadingKey(editor) })
  const canToggle = useEditorState({
    editor,
    selector: (snapshot) =>
      HEADING_ITEMS.some((item) =>
        snapshot.editor
          .can()
          .command(toggleBlockType("heading", "paragraph", { level: item.level })),
      ) || snapshot.editor.can().setNode("paragraph"),
  })

  const onSelectionChange = (keys: Selection) => {
    const key = keys === "all" ? undefined : [...keys][0]
    const heading = HEADING_ITEMS.find((item) => item.key === key)
    const chain = editor.chain().label(OPERATION_CONTRACTS.OP_SET_HEADING, "floating-toolbar", {
      level: heading?.level ?? null,
    })
    if (heading) chain.command(setBlockType("heading", { level: heading.level })).run()
    // undefined (deselected the active heading) or "normal": land on a plain paragraph.
    else chain.setNode("paragraph").run()
  }

  return (
    <Dropdown.Root>
      <Tooltip title="Headings" closeDelay={0}>
        <Button
          isDisabled={!canToggle}
          color="tertiary"
          size="xs"
          className={cx(
            "data-icon-only:px-0",
            TOOLBAR_BUTTON_CHROME,
            activeKey !== null && activeKey !== "normal" && TOOLBAR_BUTTON_ACTIVE,
          )}
          aria-label="Heading options"
          iconLeading={<IconHeadings />}
          iconTrailing={<IconChevronDown className={TOOLBAR_CHEVRON} />}
        />
      </Tooltip>
      <Dropdown.Popover placement="bottom">
        <Dropdown.Menu
          aria-label="Heading style"
          selectionMode="single"
          selectedKeys={activeKey === null ? [] : [activeKey]}
          onSelectionChange={onSelectionChange}
        >
          {HEADING_ITEMS.map((item) => (
            <HeadingItem
              key={item.key}
              id={item.key}
              label={item.label}
              shortcut={`${modKeyText}+Alt+${item.shortcut}`}
            >
              {item.glyph}
            </HeadingItem>
          ))}
          <HeadingItem id="normal" label="Normal Text" shortcut={`${modKeyText}+Alt+0`}>
            <Pilcrow02 />
          </HeadingItem>
        </Dropdown.Menu>
      </Dropdown.Popover>
    </Dropdown.Root>
  )
}

function HeadingItem({
  id,
  label,
  shortcut,
  children,
}: {
  id: Key
  label: string
  shortcut: string
  children: ReactNode
}) {
  return (
    <Dropdown.Item id={id} textValue={label} aria-label={label} selectionIndicator="none">
      {({ isSelected }) => (
        <span
          className={cx("flex w-full items-center justify-between gap-4", SELECTED_ROW(isSelected))}
        >
          <span className={cx("flex items-center gap-2", MENU_ROW_GLYPH)}>
            {children} {label}
          </span>
          <span className="text-gray-400">{shortcut}</span>
        </span>
      )}
    </Dropdown.Item>
  )
}
